import { useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm, type FieldPath, type UseFormReturn } from "react-hook-form";

import { useLocalization } from "@i18n";

import type {
  AdminStructureDetail,
  PatchAdminStructureGeometryRequest,
  StructureType,
} from "../../api/structures/structures.types";
import { usePatchAdminStructureGeometryMutation } from "../../api/structures/structures.mutations";
import {
  createAdminStructureGeometrySchema,
  mapDetailToGeometryForm,
  mapDetailToOpenPitGeometryForm,
  type AdminStructureGeometryForm,
  type AdminStructureOpenPitGeometryForm,
} from "../../schemas";
import { STRUCTURE_GEOMETRY_FORM_ID } from "./structure-modal-form-ids";
import { useOptionalAdminStructureOpenPitGeometryFormContext } from "./useAdminStructureOpenPitGeometryFormContext";

type GeometryFieldConfig = {
  name: FieldPath<AdminStructureGeometryForm>;
  labelKey: string;
};

const DAM_GEOMETRY_FIELDS: GeometryFieldConfig[] = [
  { name: "crestElevation", labelKey: "admin.structures.geometry.label.crestElevation" },
  { name: "damHeight", labelKey: "admin.structures.geometry.label.damHeight" },
  { name: "crestLength", labelKey: "admin.structures.geometry.label.crestLength" },
  { name: "crestWidth", labelKey: "admin.structures.geometry.label.crestWidth" },
  { name: "typicalBermWidth", labelKey: "admin.structures.geometry.label.typicalBermWidth" },
  {
    name: "upstreamSlopeInclination",
    labelKey: "admin.structures.geometry.label.upstreamSlopeInclination",
  },
  {
    name: "downstreamSlopeInclination",
    labelKey: "admin.structures.geometry.label.downstreamSlopeInclination",
  },
  {
    name: "betweenBermsSlopeInclination",
    labelKey: "admin.structures.geometry.label.betweenBermsSlopeInclination",
  },
  {
    name: "globalDownstreamSlopeInclination",
    labelKey: "admin.structures.geometry.label.globalDownstreamSlopeInclination",
  },
  { name: "referenceDocument", labelKey: "admin.structures.geometry.label.referenceDocument" },
];

const STOCKPILE_GEOMETRY_FIELDS: GeometryFieldConfig[] = [
  { name: "typicalBermWidth", labelKey: "admin.structures.geometry.label.typicalBermWidth" },
  {
    name: "benchSlopeInclination",
    labelKey: "admin.structures.geometry.label.benchSlopeInclination",
  },
  {
    name: "globalSlopeInclination",
    labelKey: "admin.structures.geometry.label.globalSlopeInclination",
  },
  { name: "referenceDocument", labelKey: "admin.structures.geometry.label.referenceDocument" },
];

const OPEN_PIT_GEOMETRY_TAB_FIELDS: GeometryFieldConfig[] = [
  { name: "pitHeight", labelKey: "admin.structures.geometry.label.pitHeight" },
  { name: "typicalBermWidth", labelKey: "admin.structures.geometry.label.typicalBermWidth" },
  { name: "benchHeight", labelKey: "admin.structures.geometry.label.benchHeight" },
  { name: "benchInclination", labelKey: "admin.structures.geometry.label.benchInclination" },
  {
    name: "globalSlopeInclination",
    labelKey: "admin.structures.geometry.label.globalSlopeInclination",
  },
  { name: "referenceDocument", labelKey: "admin.structures.geometry.label.referenceDocument" },
];

const fieldsForType = (structureType: StructureType): GeometryFieldConfig[] => {
  if (structureType === "stockpile") {
    return STOCKPILE_GEOMETRY_FIELDS;
  }
  if (structureType === "openPit") {
    return OPEN_PIT_GEOMETRY_TAB_FIELDS;
  }
  return DAM_GEOMETRY_FIELDS;
};

export type AdminStructureGeometryTabProps = {
  complexId: string;
  structureId: string;
  structureType: StructureType;
  canWrite: boolean;
  detail: AdminStructureDetail;
  onSaved: (detail: AdminStructureDetail) => void;
  onSavingChange?: (isSaving: boolean) => void;
};

type GeometryFormFieldsProps = {
  form: UseFormReturn<AdminStructureGeometryForm>;
  fields: GeometryFieldConfig[];
  canWrite: boolean;
};

const GeometryFormFields = ({ form, fields, canWrite }: GeometryFormFieldsProps) => {
  const { translate } = useLocalization();

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {fields.map(({ name, labelKey }) => {
        const label = translate(labelKey);
        return (
          <Controller
            key={name}
            control={form.control}
            name={name}
            render={({ field, fieldState }) => (
              <label className="group flex min-w-0 w-full flex-col gap-1">
                <span className="text-label group-aria-invalid:text-error">
                  {label}
                </span>
                <input
                  type="text"
                  aria-label={label}
                  aria-invalid={Boolean(fieldState.error) || undefined}
                  disabled={!canWrite}
                  {...field}
                  className="h-10 w-full rounded-xl border border-border bg-background px-4 text-sm shadow-sm disabled:opacity-60 aria-invalid:border-error aria-invalid:ring-[3px] aria-invalid:ring-error/20"
                />
                {fieldState.error?.message ? (
                  <span className="text-error text-sm">
                    {fieldState.error.message}
                  </span>
                ) : null}
              </label>
            )}
          />
        );
      })}
    </div>
  );
};

const AdminStructureGeometryTab = ({
  complexId,
  structureId,
  structureType,
  canWrite,
  detail,
  onSaved,
  onSavingChange,
}: AdminStructureGeometryTabProps) => {
  const openPitFormContext = useOptionalAdminStructureOpenPitGeometryFormContext();

  if (structureType === "openPit" && openPitFormContext) {
    return (
      <AdminStructureOpenPitGeometryTabWithSharedForm
        complexId={complexId}
        structureId={structureId}
        canWrite={canWrite}
        onSaved={onSaved}
        onSavingChange={onSavingChange}
        form={openPitFormContext.form}
      />
    );
  }

  return (
    <AdminStructureGeometryTabWithLocalForm
      complexId={complexId}
      structureId={structureId}
      structureType={structureType}
      canWrite={canWrite}
      detail={detail}
      onSaved={onSaved}
      onSavingChange={onSavingChange}
    />
  );
};

type AdminStructureOpenPitGeometryTabWithSharedFormProps = {
  complexId: string;
  structureId: string;
  canWrite: boolean;
  onSaved: (detail: AdminStructureDetail) => void;
  onSavingChange?: (isSaving: boolean) => void;
  form: UseFormReturn<AdminStructureOpenPitGeometryForm>;
};

const AdminStructureOpenPitGeometryTabWithSharedForm = ({
  complexId,
  structureId,
  canWrite,
  onSaved,
  onSavingChange,
  form,
}: AdminStructureOpenPitGeometryTabWithSharedFormProps) => {
  const patchMutation = usePatchAdminStructureGeometryMutation();

  useEffect(() => {
    onSavingChange?.(patchMutation.isPending);
  }, [onSavingChange, patchMutation.isPending]);

  const onSubmit = async (data: AdminStructureOpenPitGeometryForm) => {
    const saved = await patchMutation.mutateAsync({
      complexId,
      structureId,
      payload: data as PatchAdminStructureGeometryRequest,
    });
    onSaved(saved);
    form.reset(mapDetailToOpenPitGeometryForm(saved));
  };

  return (
    <form
      id={STRUCTURE_GEOMETRY_FORM_ID}
      noValidate
      onSubmit={form.handleSubmit(onSubmit, () => {
        void form.trigger(undefined, { shouldFocus: true });
      })}
      className="flex flex-col gap-4"
    >
      <GeometryFormFields
        form={form as UseFormReturn<AdminStructureGeometryForm>}
        fields={OPEN_PIT_GEOMETRY_TAB_FIELDS}
        canWrite={canWrite}
      />
    </form>
  );
};

type AdminStructureGeometryTabWithLocalFormProps = AdminStructureGeometryTabProps;

const AdminStructureGeometryTabWithLocalForm = ({
  complexId,
  structureId,
  structureType,
  canWrite,
  detail,
  onSaved,
  onSavingChange,
}: AdminStructureGeometryTabWithLocalFormProps) => {
  const { translate } = useLocalization();
  const patchMutation = usePatchAdminStructureGeometryMutation();
  const schema = useMemo(
    () => createAdminStructureGeometrySchema(translate, structureType),
    [structureType, translate],
  );

  const form = useForm<AdminStructureGeometryForm>({
    resolver: zodResolver(schema),
    mode: "onChange",
    shouldFocusError: true,
    defaultValues: mapDetailToGeometryForm(detail, structureType),
  });

  useEffect(() => {
    form.reset(mapDetailToGeometryForm(detail, structureType));
  }, [detail, form, structureType]);

  useEffect(() => {
    onSavingChange?.(patchMutation.isPending);
  }, [onSavingChange, patchMutation.isPending]);

  const onSubmit = async (data: AdminStructureGeometryForm) => {
    const saved = await patchMutation.mutateAsync({
      complexId,
      structureId,
      payload: data as PatchAdminStructureGeometryRequest,
    });
    onSaved(saved);
    form.reset(mapDetailToGeometryForm(saved, structureType));
  };

  const fields = fieldsForType(structureType);

  return (
    <form
      id={STRUCTURE_GEOMETRY_FORM_ID}
      noValidate
      onSubmit={form.handleSubmit(onSubmit, () => {
        void form.trigger(undefined, { shouldFocus: true });
      })}
      className="flex flex-col gap-4"
    >
      <GeometryFormFields form={form} fields={fields} canWrite={canWrite} />
    </form>
  );
};

export default AdminStructureGeometryTab;
